"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React, { FC, useContext } from "react";

import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Icon from "@/components/ui/Icon/Icon";
import { TemplateContext } from "@/context/TemplateContext";
import useMediaQuery from "@/hooks/useMediaQuery";
import { EditorialTemplateHeroProps } from "@/types";

import Breadcrumb from "../Breadcrumb/Breadcrumb";

const EditorialTemplateHero: FC<EditorialTemplateHeroProps> = ({ entry }) => {
  const t = useTranslations();
  const locale = useLocale();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { fixedHeader } = useContext(TemplateContext);

  if (entry.tags) {
    entry.tags = entry?.tags.slice(0, 3);
  }

  const share = async (shareData: any) => {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert(t("copy.link"));
      console.log("share not supported");
    }
  };

  const mediaExist = !!(entry.image || entry.video);

  return (
    <div
      className={cn(
        `p-m md:p-0 ${entry?.type === "editorialTemplateHero" ? "md:aspect-[3/1]" : "aspect-[2/1]"}`,
        mediaExist && "relative bg-cover bg-center text-white md:h-auto",
        isMobile ? "h-[33vh]" : "h-svh",
      )}
      style={entry?.image && { backgroundImage: `url(${entry?.image?.url}` }}
    >
      {entry?.video && (
        <div className="absolute w-full h-full">
          <video className="h-full w-full object-cover" muted autoPlay loop playsInline title={entry?.video?.title}>
            <source src={entry?.video?.url} type="video/mp4" />
          </video>
        </div>
      )}

      {mediaExist && <div className="absolute z-10 w-full h-full img-overlay-article-hero top-0 left-0 pt-"></div>}
      <div
        className={cn(
          "px-s md:px-0 md:w-20\\/24 md:mx-auto pb-xxl text-center",
          mediaExist ? "pt-m md:pt-xl" : "pt-l md:pt-3xl",
          fixedHeader ? "!pt-xxl md:!pt-[72px]" : "!pt-0 md:pt-xl",
        )}
      >
        <div className={cn(!mediaExist && "md:flex md:items-center md:justify-center md:relative md:mb-xxl")}>
          {mediaExist ? (
            <div className="flex items-center justify-between z-20 mb-l relative">
              {entry?.breadcrumb && <Breadcrumb entry={entry?.breadcrumb} disabledBehaviour={true} />}
              {/* <button
                onClick={() => share({ title: entry.title || entry.logo.title, text: entry.title || entry.logo.title, url: window.location.href })}
              >
                <Icon name="share" size={"20"} className={cn(mediaExist ? "stroke-white" : "stroke-black")}></Icon>
              </button> */}
            </div>
          ) : (
            <AnchorLink
              href={getLink(entry?.breadcrumb?.breadcrumbItems[entry?.breadcrumb?.breadcrumbItems?.length - 1], locale).toString()}
              className="underline underline-offset-2 text-s font-medium inline-block md:absolute md:left-0 md:top-[50%] md:translate-y-[-50%] mb-xl md:mb-0"
            >
              <>{t("label.common.backTo", { label: entry?.breadcrumb?.breadcrumbItems[entry?.breadcrumb?.breadcrumbItems?.length - 1]?.label })}</>
            </AnchorLink>
          )}
          <div className={cn(!mediaExist && "md:flex items-center md:gap-xxl ")}>
            {entry?.logo && (
              <Image
                src={entry?.logo?.url}
                width={170}
                height={80}
                alt={entry?.logo?.title || ""}
                className={cn(
                  "mx-auto my-xxl md:m-0",
                  mediaExist && "absolute top-[45%] translate-y-[-50%] left-[50%] translate-x-[-50%] z-10 md:w-5\\/24",
                )}
              />
            )}

            <div
              className={cn(
                mediaExist &&
                  `absolute ${isMobile ? "bottom-[0]" : "bottom-[40px] md:bottom-[24px]"} z-10 left-[50%] translate-x-[-50%] w-full md:w-20\\/24`,
              )}
            >
              {entry?.title && (
                <h1 className={cn("font-primary uppercase text-l md:text-xl mb-xl ", !mediaExist ? "md:mb-0" : "md:mb-l font-medium")}>
                  {entry?.title}
                </h1>
              )}
              {mediaExist && entry?.tags?.length && (
                <div className="flex items-center gap-xs justify-center">
                  {entry?.tags?.map((tag, i) => (
                    <span className="text-s uppercase rounded p-[6px] text-black golden-gradient" key={`bgTag-${i}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {!mediaExist && entry?.abstract && <p className="font-secondary-light text-[14px]">{entry?.abstract}</p>}
      </div>
    </div>
  );
};

export default EditorialTemplateHero;
